import React, { useState } from 'react';
import { Send, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { createInquiry } from '../../services/inquiryService';
import { t } from '../../i18n';
import { Language } from '../../types';

interface InquiryFormProps {
    lang: string;
    onSuccess: () => void;
}

export const InquiryForm: React.FC<InquiryFormProps> = ({ lang, onSuccess }) => {
    const { user } = useAuth();
    const { showToast } = useToast();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState('general');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const categories = [
        { id: 'general', label: t(lang as Language, 'inquiryGeneral') },
        { id: 'account', label: t(lang as Language, 'inquiryAccount') },
        { id: 'content', label: t(lang as Language, 'inquiryContent') },
        { id: 'error', label: t(lang as Language, 'inquiryError') },
        { id: 'suggestion', label: t(lang as Language, 'inquirySuggestion') }
    ];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) {
            showToast('error', t(lang as Language, 'loginRequired'));
            return;
        }
        if (!title.trim() || !content.trim()) {
            showToast('error', t(lang as Language, 'enterTitleAndContent'));
            return;
        }

        setIsSubmitting(true);
        try {
            await createInquiry(user.id, title, content, category);

            showToast('success', t(lang as Language, 'inquirySubmitted'));
            setTitle('');
            setContent('');
            onSuccess();
        } catch (error: any) {
            console.error(error);
            showToast('error', t(lang as Language, 'errorInquiry'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">
                    {t(lang as Language, 'category')}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            type="button"
                            onClick={() => setCategory(cat.id)}
                            className={`px-4 py-3 rounded-2xl text-xs font-bold transition-all border-2 ${
                                category === cat.id
                                    ? 'border-rose-500 bg-rose-50 text-rose-500 shadow-sm'
                                    : 'border-slate-50 bg-slate-50 text-slate-400 md:hover:border-slate-100'
                            }`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">
                    {t(lang as Language, 'title')}
                </label>
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t(lang as Language, 'titlePlaceholder')}
                    className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-4 text-sm font-bold text-slate-900 focus:outline-none focus:border-rose-500/20 focus:bg-white transition-all"
                />
            </div>

            <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">
                    {t(lang as Language, 'content')}
                </label>
                <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={t(lang as Language, 'inquiryPlaceholder')}
                    rows={6}
                    className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-4 text-sm font-bold text-slate-900 focus:outline-none focus:border-rose-500/20 focus:bg-white transition-all resize-none"
                />
            </div>

            <div className="bg-amber-50 rounded-2xl p-4 flex gap-3 items-start">
                <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                <p className="text-[11px] text-amber-700 font-medium leading-relaxed">
                    {t(lang as Language, 'inquiryNote')}
                </p>
            </div>

            <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-slate-900 text-white py-5 rounded-[24px] font-black text-sm uppercase tracking-[0.2em] shadow-xl md:hover:bg-rose-500 active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-3"
            >
                {isSubmitting ? (
                    <Loader2 size={20} className="animate-spin" />
                ) : (
                    <>
                        <Send size={18} />
                        {t(lang as Language, 'submitInquiry')}
                    </>
                )}
            </button>
        </form>
    );
};
